/**
 * Phase 3 — Sleeping City physics worker protocol.
 *
 * Transferable ArrayBuffer ping-pong (no SharedArrayBuffer / COOP/COEP).
 * Worker is sole owner of Box3D World + FixedStepper; main holds a snapshot
 * mirror for render / probes / pure read helpers.
 */

import type { YardCaps, YardImpact, YardStructure } from './yardPhysics';

/** Protocol version — bump when wire shape breaks. */
export const YARD_WORKER_PROTOCOL_VERSION = 1;

/** Coalesce unbounded wall-clock gaps so FixedStepper never sees huge catch-up. */
export const YARD_WORKER_MAX_PENDING_DT = 0.1;

/** Max tool/lifecycle commands queued while a frame is in-flight. */
export const YARD_WORKER_MAX_CMD_QUEUE = 32;

// ---------------------------------------------------------------------------
// Snapshot scalars (structured-clone side of the ping-pong message)
// ---------------------------------------------------------------------------

export interface YardSnapshotMeta {
  sequence: number;
  stepsThisFrame: number;
  stepMs: number;
  awakeCount: number;
  awakeBrickCount: number;
  everAwakeCount: number;
  retiredCount: number;
  dynamicBrickCount: number;
  bodyCountTotal: number;
  fixedTime: number;
  simTime: number;
  substepsUsed: number;
  maxStepsUsed: number;
  structure: YardStructure;
  structureVersion: number;
  slowMo: boolean;
  ballBodyId: number;
  brickCount: number;
  chainCount: number;
  cannonballCount: number;
  prevValid: boolean;
  chainPeakSpeed: number;
  chainPeakDisp: number;
  chainSampleFrames: number;
  chainLastMaxSpeed: number;
  profileKey: string;
  /** Byte lengths of the transferable payload (for capacity checks / tests). */
  transformsBytes: number;
  brickSleepBytes: number;
  brickEverAwakeBytes: number;
  brickRetiredBytes: number;
  brickBodyIdsBytes: number;
  chainBodyIdsBytes: number;
  cannonballBodyIdsBytes: number;
}

/** Free buffers main returns to the worker for the next write (ping-pong). */
export interface YardFreeBuffers {
  transforms?: ArrayBuffer;
  brickSleepStates?: ArrayBuffer;
  brickEverAwake?: ArrayBuffer;
  brickRetired?: ArrayBuffer;
  brickBodyIds?: ArrayBuffer;
  chainBodyIds?: ArrayBuffer;
  cannonballBodyIds?: ArrayBuffer;
}

/** Transferable snapshot payload (worker → main). */
export interface YardSnapshotPayload {
  transforms: ArrayBuffer;
  brickSleepStates: ArrayBuffer;
  brickEverAwake: ArrayBuffer;
  brickRetired: ArrayBuffer;
  brickBodyIds: ArrayBuffer;
  chainBodyIds: ArrayBuffer;
  cannonballBodyIds: ArrayBuffer;
}

// ---------------------------------------------------------------------------
// Commands (main → worker) — applied before the paired frame step
// ---------------------------------------------------------------------------

export type YardWorkerCmd =
  | { type: 'cascadeLaunch' }
  | {
      type: 'spawnCannon';
      origin: { x: number; y: number; z: number };
      dir: { x: number; y: number; z: number };
    }
  | { type: 'boom'; x: number; y: number; z: number }
  | {
      type: 'beginDrag';
      targetBodyId: number;
      x: number;
      y: number;
      z: number;
    }
  | { type: 'updateDrag'; x: number; y: number; z: number }
  | { type: 'endDrag' }
  | { type: 'setStructure'; structure: YardStructure }
  | { type: 'reset' }
  | { type: 'setSlowMo'; slowMo: boolean }
  | { type: 'calmWreckingBall' }
  | { type: 'sleepSettledCascadeBricks' }
  | { type: 'forceSleepAllCascadeBricks'; hard?: boolean }
  | { type: 'resetChainStability' };

export type MainToWorkerMessage =
  | {
      type: 'init';
      protocolVersion: number;
      requestId: number;
      structure: YardStructure;
      bodyLimit: number;
    }
  | {
      type: 'frame';
      sequence: number;
      dt: number;
      cascadeOptimizationActive: boolean;
      cmds: YardWorkerCmd[];
      freeBuffers?: YardFreeBuffers;
    }
  | { type: 'dispose' };

export type WorkerToMainMessage =
  | {
      type: 'ready';
      requestId: number;
      protocolVersion: number;
      caps: YardCaps;
      structure: YardStructure;
      bodyLimit: number;
      profileKey: string;
      brickColors: string[];
      ballBodyId: number;
      bodyCountTotal: number;
      brickCount: number;
      chainCount: number;
      structureVersion: number;
    }
  | {
      type: 'snapshot';
      meta: YardSnapshotMeta;
      impacts: YardImpact[];
      /** Present when structureVersion changed since last snapshot (incl. first). */
      brickColors?: string[];
      buffers: YardSnapshotPayload;
    }
  | { type: 'error'; message: string; requestId?: number };

// ---------------------------------------------------------------------------
// Pure helpers (unit-tested)
// ---------------------------------------------------------------------------

/** Whether this structure+limit profile should use the physics worker. */
export function shouldUseYardPhysicsWorker(
  structure: YardStructure,
  bodyLimit: number,
  /** Normal yard max bricks — worker only for Sleeping City scale. */
  maxBricksMainThread: number,
): boolean {
  return structure === 'cascade' && bodyLimit > maxBricksMainThread;
}

/**
 * Coalesce frame dt while a worker frame is in-flight.
 * Caps accumulation so a background tab does not dump seconds of sim at once.
 */
export function coalescePendingDt(current: number, delta: number, maxPending = YARD_WORKER_MAX_PENDING_DT): number {
  if (!(delta > 0)) return current;
  return Math.min(maxPending, current + delta);
}

/**
 * Append a command without unbounded growth: drop oldest non-drag updates when full.
 * updateDrag coalesces to the latest target (one slot).
 */
export function enqueueYardWorkerCmd(
  queue: YardWorkerCmd[],
  cmd: YardWorkerCmd,
  maxLen = YARD_WORKER_MAX_CMD_QUEUE,
): YardWorkerCmd[] {
  if (cmd.type === 'updateDrag') {
    const last = queue[queue.length - 1];
    if (last?.type === 'updateDrag') {
      last.x = cmd.x;
      last.y = cmd.y;
      last.z = cmd.z;
      return queue;
    }
  }
  queue.push(cmd);
  while (queue.length > maxLen) {
    // Prefer dropping intermediate updateDrag / resetChainStability over lifecycle cmds.
    const dropIdx = queue.findIndex((c) => c.type === 'updateDrag' || c.type === 'resetChainStability');
    if (dropIdx >= 0) queue.splice(dropIdx, 1);
    else queue.shift();
  }
  return queue;
}

/** True when a snapshot sequence should replace the currently applied one. */
export function shouldApplySnapshot(latestApplied: number, incoming: number): boolean {
  return incoming >= latestApplied;
}

/** Byte length for N float32 transforms (7 floats/body). */
export function transformsByteLength(bodyCount: number): number {
  return Math.max(0, bodyCount) * 7 * 4;
}

/** Ensure an ArrayBuffer is large enough; reallocate when short or detached. */
export function ensureBufferCapacity(existing: ArrayBuffer | undefined, bytes: number): ArrayBuffer {
  if (existing && existing.byteLength >= bytes) {
    try {
      // Detached buffers throw on access in modern engines.
      if (existing.byteLength >= 0 && !(existing as ArrayBuffer & { detached?: boolean }).detached) {
        return existing;
      }
    } catch {
      // fall through
    }
  }
  return new ArrayBuffer(Math.max(bytes, 0));
}

/**
 * Copy typed source into a transferable ArrayBuffer of at least source.byteLength.
 * Returns the filled buffer (may reallocate).
 */
export function copyIntoTransferable(
  source: ArrayBufferView,
  free: ArrayBuffer | undefined,
): ArrayBuffer {
  const bytes = source.byteLength;
  const buf = ensureBufferCapacity(free, bytes);
  new Uint8Array(buf, 0, bytes).set(new Uint8Array(source.buffer, source.byteOffset, bytes));
  // If capacity > need, return a slice that matches exact length for consumers
  // that size views from byteLength. Prefer exact when oversized free buffer.
  if (buf.byteLength === bytes) return buf;
  // Keep oversized free buffers for ping-pong reuse — views use meta lengths.
  return buf;
}

/** Build transfer list for postMessage (only defined ArrayBuffers). */
export function snapshotTransferList(buffers: YardSnapshotPayload): Transferable[] {
  return [
    buffers.transforms,
    buffers.brickSleepStates,
    buffers.brickEverAwake,
    buffers.brickRetired,
    buffers.brickBodyIds,
    buffers.chainBodyIds,
    buffers.cannonballBodyIds,
  ];
}

/** Free-buffer transfer list (main → worker), skipping undefined. */
export function freeBuffersTransferList(free: YardFreeBuffers | undefined): Transferable[] {
  if (!free) return [];
  const out: Transferable[] = [];
  if (free.transforms) out.push(free.transforms);
  if (free.brickSleepStates) out.push(free.brickSleepStates);
  if (free.brickEverAwake) out.push(free.brickEverAwake);
  if (free.brickRetired) out.push(free.brickRetired);
  if (free.brickBodyIds) out.push(free.brickBodyIds);
  if (free.chainBodyIds) out.push(free.chainBodyIds);
  if (free.cannonballBodyIds) out.push(free.cannonballBodyIds);
  return out;
}
